import {
  addDays,
  formatDate,
  relativeTime,
  sessionActions,
  sessionPhase,
  WEEKDAYS,
  type Session,
  type SessionAction,
} from "@love-cpmd/core";
import { Badge, Button, cn, Drawer, EmptyState, Skeleton } from "@love-cpmd/ui";
import {
  ArrowLeftRight,
  CalendarDays,
  CalendarX2,
  CircleCheck,
  Clock3,
  FileText,
  Info,
  MapPin,
  NotebookPen,
  PencilLine,
  Sparkles,
  SquarePen,
  UserRoundCog,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { describeActivity } from "../../lib/activity";
import { useAccess } from "../../lib/auth";
import { useOverlay } from "../../lib/hooks";
import { useActivity, useWorld, type World } from "../../lib/queries";
import { CategoryChip, CountdownBadge, phaseHint, PresenterAvatar, presenterMeta, SessionBadges } from "../common/session-bits";

const ACTION_META: Record<SessionAction, { label: string; icon: LucideIcon; tone: "primary" | "secondary" | "danger" }> = {
  editTopic: { label: "Edit topic", icon: PencilLine, tone: "primary" },
  change: { label: "Change schedule (reschedule / swap)", icon: ArrowLeftRight, tone: "secondary" },
  cancel: { label: "Cancel session", icon: CalendarX2, tone: "danger" },
  markDone: { label: "Mark as done", icon: CircleCheck, tone: "primary" },
  replacement: { label: "Replacement presenter", icon: UserRoundCog, tone: "secondary" },
  markCancelled: { label: "Mark as cancelled", icon: CalendarX2, tone: "danger" },
  addSummary: { label: "Add summary", icon: NotebookPen, tone: "primary" },
  logExtra: { label: "Log extra sharing", icon: Sparkles, tone: "secondary" },
  viewSummary: { label: "Read summary", icon: FileText, tone: "primary" },
  editSummary: { label: "Edit summary", icon: SquarePen, tone: "secondary" },
};

/** Change actions open as dialogs on top of the panel; summary actions go to the summary pages. */
const ACTION_TARGET: Partial<Record<SessionAction, string>> = {
  editTopic: "edit-topic",
  change: "change",
  cancel: "cancel",
  markDone: "mark-done",
  replacement: "replacement",
  markCancelled: "cancel",
  logExtra: "log-extra",
};

export function SessionPanel({ sessionId, open, children }: { sessionId: string; open: boolean; children?: ReactNode }) {
  const { world, isLoading } = useWorld();
  const { closeSession } = useOverlay();
  const session = world?.sessionsById.get(sessionId);
  const number = session ? world?.numbers.get(session.type === "spontaneous" ? session.parentSessionId ?? "" : session.id) : undefined;
  // Nested dialogs must register after the panel, or the panel ends up on top and the dialog turns inert.
  // React runs child effects first, so when both open together (deep link, "Change schedule" outside the
  // panel) the action dialog waits one tick for the panel.
  const [childrenReady, setChildrenReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setChildrenReady(false);
      return;
    }
    const t = window.setTimeout(() => setChildrenReady(true), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  return (
    <Drawer
      open={open}
      onClose={closeSession}
      title="Session details"
      subtitle={session ? `CPMD Weekly Sharing${number ? ` #${number}` : ""}` : undefined}
      icon={<Info />}
    >
      {isLoading || !world ? (
        <div className="flex flex-col gap-4 p-5">
          <Skeleton className="h-20" />
          <Skeleton className="h-28" />
          <Skeleton className="h-16" />
          <Skeleton className="h-24" />
        </div>
      ) : !session ? (
        <EmptyState icon={<CalendarX2 />} title="This session no longer exists" description="It may have been removed. Check the schedule for the latest dates." />
      ) : (
        <SessionPanelBody session={session} world={world} />
      )}
      {childrenReady && children}
    </Drawer>
  );
}

function Block({ label, aside, children }: { label: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-label-sm font-bold tracking-wider text-on-surface-variant uppercase">{label}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function SessionPanelBody({ session, world }: { session: Session; world: World }) {
  const { user, show, enabled } = useAccess();
  const { openAction, openSession } = useOverlay();
  const navigate = useNavigate();
  const { data: activity = [] } = useActivity();
  const summary = world.summaryBySession.get(session.id);
  const phase = sessionPhase(session, world.today);
  const category = session.categoryId ? world.categoriesById.get(session.categoryId) : undefined;
  const actions = sessionActions(session, world.today, !!summary).filter((a) =>
    a === "viewSummary" ? true : a === "addSummary" || a === "editSummary" ? show("summary.edit") : show("schedule.edit"),
  );
  const isMine = session.presenter.kind === "member" && session.presenter.id === user?.id;
  const deadline = addDays(session.date, -3);
  const siblings = world.sessions.filter((s) => s.type === "spontaneous" && s.parentSessionId === session.id);
  const parent = session.parentSessionId ? world.sessionsById.get(session.parentSessionId) : undefined;
  const history = activity
    .filter((a) => a.target.id === session.id || a.data.aSession === session.id || a.data.bSession === session.id || a.data.sessionId === session.id)
    .slice(0, 5);
  const lastLabel = session.labels.at(-1);

  const run = (a: SessionAction) => {
    if (a === "addSummary" || a === "editSummary") navigate(`/summary/${session.id}/edit`);
    else if (a === "viewSummary") navigate(`/summary/${session.id}`);
    else openAction(ACTION_TARGET[a]!);
  };

  return (
    <div className="flex flex-col gap-6 p-4 md:p-5">
      {/* Presenter */}
      <div className="flex items-center justify-between gap-3 rounded-2xl bg-surface-container-low p-3.5">
        <div className="flex min-w-0 items-center gap-3">
          <PresenterAvatar presenter={session.presenter} world={world} size="lg" />
          <div className="min-w-0">
            <p className="truncate text-label-lg text-on-surface">{world.name(session.presenter)}</p>
            <p className="truncate text-body-sm text-on-surface-variant">{presenterMeta(session.presenter, world)}</p>
          </div>
        </div>
        <Badge tone={isMine ? "brand" : "secondary"} size="sm">
          {isMine ? "You" : "Presenter"}
        </Badge>
      </div>
      {session.type === "replacement" && session.scheduledPresenter && (
        <p className="-mt-3 flex items-center gap-2 rounded-xl bg-replacement-soft px-3 py-2 text-body-sm text-replacement">
          <UserRoundCog className="size-4 shrink-0" aria-hidden />
          Replacing {world.name(session.scheduledPresenter)}, who was scheduled originally.
        </p>
      )}
      {parent && (
        <button
          type="button"
          onClick={() => openSession(parent.id)}
          className="-mt-3 flex items-center gap-2 rounded-xl bg-spontaneous-soft px-3 py-2 text-left text-body-sm text-spontaneous hover:underline"
        >
          <Sparkles className="size-4 shrink-0" aria-hidden />
          Extra sharing during {world.name(parent.presenter, "short")}'s session
        </button>
      )}

      {/* Schedule */}
      <Block label="Schedule" aside={<div className="flex flex-wrap justify-end gap-1.5"><SessionBadges session={session} /></div>}>
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-outline-variant/50 bg-surface-container-lowest p-3.5 shadow-card">
          <div className="flex min-w-0 items-center gap-2.5">
            <CalendarDays className="size-5 shrink-0 text-primary" aria-hidden />
            <span className="text-headline-sm text-on-surface">{formatDate(session.date, "long")}</span>
          </div>
          {(phase === "upcoming" || phase === "today") && <CountdownBadge date={session.date} today={world.today} />}
        </div>
        <div className="flex flex-col gap-1 px-1 text-body-sm text-on-surface-variant">
          <span className="flex items-center gap-2">
            <Clock3 className="size-4 shrink-0" aria-hidden />
            {world.settings.startTime}–{world.settings.endTime} WIB · {WEEKDAYS[world.settings.weekday]} Weekly Sharing
          </span>
          <span className="flex items-center gap-2">
            <MapPin className="size-4 shrink-0" aria-hidden />
            {world.settings.location}
          </span>
          {phase !== "upcoming" && phase !== "today" && <span className="pl-6 text-on-surface-variant/90">{phaseHint(session, world.today)}</span>}
          {lastLabel && session.status === "scheduled" && (
            <span className="flex items-center gap-2 text-swap">
              <ArrowLeftRight className="size-4 shrink-0" aria-hidden />
              {lastLabel.kind === "swapped"
                ? `Swapped with ${world.name(lastLabel.withPresenter, "short")} (was ${formatDate(lastLabel.from, "dayMonth")})`
                : `Moved from ${formatDate(lastLabel.from, "dayMonth")}`}
            </span>
          )}
        </div>
      </Block>

      {/* Category & topic */}
      <Block label="Category">
        <div>
          <CategoryChip category={category} size="md" />
        </div>
      </Block>
      <Block label="Topic" aside={!session.topic && session.status === "scheduled" ? <span className="text-label-sm text-on-surface-variant italic">Optional for now</span> : undefined}>
        {session.topic ? (
          <p className="rounded-2xl bg-surface-container-low px-4 py-3 text-headline-sm text-on-surface">{session.topic}</p>
        ) : (
          <div className="flex items-start gap-2.5 rounded-2xl bg-surface-container-low px-4 py-3">
            <PencilLine className="mt-0.5 size-5 shrink-0 text-outline" aria-hidden />
            <div className="flex flex-col">
              <span className="text-body-md text-on-surface-variant italic">Not filled in yet</span>
              {session.status === "scheduled" && (
                <span className="text-body-sm text-on-surface-variant/90">Please add it by {formatDate(deadline, "medium")} (D-3).</span>
              )}
            </div>
          </div>
        )}
      </Block>

      {/* Actions by state (plan table) */}
      {actions.length > 0 && (
        <div className="flex flex-col gap-2">
          {actions.map((a, i) => {
            const meta = ACTION_META[a];
            const Icon = meta.icon;
            const disabled = a !== "viewSummary" && !enabled(a === "addSummary" || a === "editSummary" ? "summary.edit" : "schedule.edit");
            return (
              <Button
                key={a}
                block
                size={i === 0 ? "lg" : "md"}
                variant={meta.tone === "danger" ? "danger" : i === 0 ? "primary" : "tonal"}
                leading={<Icon />}
                onClick={() => run(a)}
                disabled={disabled}
              >
                {meta.label}
              </Button>
            );
          })}
        </div>
      )}
      {session.status === "cancelled" && (
        <p className="rounded-2xl bg-cancelled-soft px-4 py-3 text-body-sm text-cancelled">
          This session was cancelled{session.cancelReason ? ` — ${session.cancelReason}` : ""}. It is kept for the record only.
        </p>
      )}

      {/* Extra sharing on the same day */}
      {siblings.length > 0 && (
        <Block label="Also shared this day">
          <ul className="flex flex-col gap-1.5">
            {siblings.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => openSession(s.id)}
                  className="flex w-full items-center gap-3 rounded-xl bg-spontaneous-soft/70 px-3 py-2.5 text-left hover:bg-spontaneous-soft"
                >
                  <PresenterAvatar presenter={s.presenter} world={world} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-label-md text-on-surface">{world.name(s.presenter)}</span>
                    <span className="block truncate text-body-sm text-on-surface-variant">{s.topic}</span>
                  </span>
                  <Sparkles className="size-4 text-spontaneous" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {/* History */}
      {history.length > 0 && (
        <Block label="History">
          <ol className="flex flex-col gap-3 rounded-2xl bg-surface-container-low p-4">
            {history.map((h) => {
              const view = describeActivity(h, world);
              return (
                <li key={h.id} className="flex items-start gap-2.5">
                  <span className="mt-1.5 size-2 shrink-0 rounded-full bg-secondary" aria-hidden />
                  <span className="flex flex-col">
                    <span className="text-body-sm font-semibold text-on-surface">{relativeTime(h.at)}</span>
                    <span className="text-body-sm text-on-surface-variant">
                      {world.memberName(h.actorId, "short")} {view.text}.
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
        </Block>
      )}

      {/* Summary link */}
      <div className={cn("flex items-center gap-3 rounded-2xl p-4", summary ? "bg-completed-soft" : "bg-surface-container")}>
        <FileText className={cn("size-6 shrink-0", summary ? "text-completed" : "text-outline")} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-label-md text-on-surface">Summary & takeaways</p>
          <p className="text-body-sm text-on-surface-variant">
            {summary
              ? `${summary.keyTakeaways.length} key takeaways · updated ${relativeTime(summary.updatedAt)}`
              : session.status === "completed"
                ? "No summary yet."
                : session.status === "cancelled"
                  ? "Cancelled sessions don't need a summary."
                  : "Available after the session is done."}
          </p>
        </div>
        {summary && (
          <Link to={`/summary/${session.id}`} className="shrink-0 text-label-md text-primary hover:underline">
            Open
          </Link>
        )}
      </div>
    </div>
  );
}
